import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';

// One admin-set daily window per buyer (backend `dispatchDailyWindow`, owner 2026-11-01):
// a time range that repeats every day until removed. Inside it the chosen mode REPLACES the
// buyer's hours and Sea / Air switches: Off = no cards (Max's ID must not order too much);
// Sea / Air / Both = in for exactly those modes, even when their hours say off (Frong takes
// air 14:00–15:00). The buyer is never told — nothing is sent and their page does not show it.

const half = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`);
const START_TIMES = half; // 00:00 … 23:30
const END_TIMES = [...half.slice(1), '24:00']; // 00:30 … 24:00

const MODE_LABEL = { off: 'Off', sea: 'Sea only', air: 'Air only', both: 'Sea + Air' };
const NEW_WINDOW = { start: '12:00', end: '15:00', mode: 'off' };

export const windowSummary = (w) => (w ? `${MODE_LABEL[w.mode] || w.mode} ${w.start}–${w.end} daily` : null);

export default function DailyWindowDialog({ buyer, onClose, onSave }) {
  const [win, setWin] = useState(NEW_WINDOW);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!buyer) return;
    setWin(buyer.dispatchDailyWindow ? { ...NEW_WINDOW, ...buyer.dispatchDailyWindow } : NEW_WINDOW);
    setError(null);
  }, [buyer]);

  const set = (field, value) => setWin((prev) => ({ ...prev, [field]: value }));

  const save = async (value) => {
    if (value && value.start === value.end) {
      setError('Start and end must differ.');
      return;
    }
    setError(null);
    setSaving(true);
    await onSave(value);
    setSaving(false);
  };

  return (
    <Dialog open={Boolean(buyer)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Daily window — {buyer?.label}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Repeats every day until you remove it. Thai time, 30-minute steps; an end before the start runs past midnight.
          Inside the window this choice replaces the buyer's hours and Sea / Air switches. Outside it nothing changes.
          The buyer is not told — their own page keeps showing their normal hours.
        </Typography>
        <ToggleButtonGroup exclusive size="small" value={win.mode} onChange={(e, v) => { if (v) set('mode', v); }}>
          {Object.entries(MODE_LABEL).map(([value, label]) => <ToggleButton key={value} value={value}>{label}</ToggleButton>)}
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary">
          {win.mode === 'off'
            ? 'No card in this range, whatever their hours, shifts or today\'s Open say.'
            : 'In the rotation for exactly these modes, even when their hours say off. Not first — ranked like anyone. Big items, Smile / Tama, caps and burst still apply.'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Select id="daily-window-start" size="small" value={win.start} onChange={(e) => set('start', e.target.value)}>
            {START_TIMES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
          –
          <Select id="daily-window-end" size="small" value={win.end} onChange={(e) => set('end', e.target.value)}>
            {END_TIMES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        {buyer?.dispatchDailyWindow && (
          <Button color="inherit" disabled={saving} onClick={() => save(null)} sx={{ mr: 'auto' }}>Remove</Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" disabled={saving} onClick={() => save(win)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
